import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { api, ApiError } from '../lib/api.js';
import { NxMark } from '../components/icons.js';

/**
 * Tela "Esqueci minha senha".
 *
 * Regra de UX: a resposta é SEMPRE genérica ("se o e-mail existir, um link
 * foi enviado"), independente do backend ter achado o usuário ou não. Isso
 * evita enumeração de contas via a tela pública.
 */
export function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await api.passwordRecovery.forgot(email.trim());
      setSent(true);
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.status === 503) {
          setError('A recuperação de senha não está disponível no momento.');
        } else if (err.status === 429) {
          setError('Muitas tentativas. Aguarde alguns minutos.');
        } else if (err.status === 400) {
          setError('E-mail inválido.');
        } else {
          setError('Não foi possível enviar. Tente novamente.');
        }
      } else {
        setError('Erro de rede.');
      }
    } finally {
      setSubmitting(false);
    }
  }

  const year = new Date().getFullYear();

  return (
    <div className="nx-login">
      <div className="nx-login__stage">
        <div className="nx-login__hero">
          <span className="nx-login__hero-eyebrow">Acesso</span>
          <h2 className="nx-login__hero-title">
            Recuperar acesso <em>ao console</em>.
          </h2>
          <p className="nx-login__hero-lead">
            Informe o e-mail cadastrado. Se ele existir, enviaremos um link
            para você definir uma nova senha. O link tem validade curta e só
            pode ser usado uma vez.
          </p>
        </div>

        <div className="nx-login__card">
          <div className="nx-login__card-head">
            <div className="nx-wordmark nx-login__brand" aria-label="Nxvolt">
              <span className="nx-wordmark__mark">
                <NxMark size={30} />
              </span>
              <span className="nx-wordmark__text">
                <b>NX</b>
                <i>volt</i>
              </span>
            </div>
            <div className="nx-login__parent">uma solução Noxxon Sat</div>
          </div>

          <h1 className="nx-login__title">Esqueci minha senha</h1>

          {sent ? (
            <>
              <div className="nx-success" role="status" style={{ marginTop: 'var(--nx-space-4)' }}>
                Se o e-mail existir em nossa base, enviamos um link para
                redefinir a senha. Verifique sua caixa de entrada (e a pasta
                de spam) nos próximos minutos.
              </div>
              <div style={{ marginTop: 'var(--nx-space-6)' }}>
                <Link to="/login" className="nx-btn nx-btn--full">
                  Voltar ao login
                </Link>
              </div>
            </>
          ) : (
            <>
              <p className="nx-login__hint">
                Use o mesmo e-mail que você utiliza para entrar no console.
              </p>
              <form onSubmit={onSubmit} noValidate>
                <label className="nx-field">
                  <span>E-mail</span>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    autoComplete="username"
                    placeholder="voce@empresa.com"
                    required
                    autoFocus
                  />
                </label>

                {error && (
                  <div role="alert" className="nx-error">
                    {error}
                  </div>
                )}

                <div style={{ marginTop: 'var(--nx-space-6)' }}>
                  <button
                    className="nx-btn nx-btn--full"
                    type="submit"
                    disabled={submitting || email.trim().length === 0}
                  >
                    {submitting ? 'Enviando…' : 'Enviar link'}
                  </button>
                </div>
              </form>

              <div
                style={{
                  marginTop: 'var(--nx-space-4)',
                  textAlign: 'center',
                }}
              >
                <Link to="/login" className="nx-btn nx-btn--link">
                  ← Voltar ao login
                </Link>
              </div>
            </>
          )}

          <div className="nx-login__footer">© {year} Noxxon Sat · Nxvolt</div>
        </div>
      </div>
    </div>
  );
}
